import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, InlineStack, TextField, DropZone, Text, Button, Icon, Spinner } from '@shopify/polaris';
import { MagicMinor, UploadMajor, ImportMinor } from '@shopify/polaris-icons';
import { BlobServiceClient } from '../services/blobStorageCompat';
import { authService } from '../services/authService';
import { getServerURL } from '../services/config';

const AIDesignMultipleTestPage = ({ embedded = false, setToastMessage, setToastActive }) => {
  // Faces (multiple)
  const [facesUrls, setFacesUrls] = useState(['']);
  const [faceFiles, setFaceFiles] = useState([]);
  const [faceAzureMap, setFaceAzureMap] = useState({}); // key -> url

  // Mask (single)
  const [maskUrl, setMaskUrl] = useState('');

  const [maskFiles, setMaskFiles] = useState([]);

  const [azureSettings, setAzureSettings] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [results, setResults] = useState([]); // successfulResults
  const [selectedPreviewUrl, setSelectedPreviewUrl] = useState('');

  const [maskAzureUrl, setMaskAzureUrl] = useState('');

  const [maskFileKey, setMaskFileKey] = useState('');

  const previewContainerRef = useRef(null);

  const loadAzureSettings = useCallback(async () => {
    try {
      const baseUrl = getServerURL();
      const resp = await fetch(`${baseUrl}/settings/storage/client`, {
        headers: authService.getAuthHeaders(),
      });
      if (!resp.ok) return null;
      const json = await resp.json();
      if (json?.success && json?.data && json.data.AZURE_BLOB_BASE_URL && json.data.AZURE_SAS_TOKEN) {
        setAzureSettings(json.data);
        return json.data;
      }
      return null;
    } catch (_e) {
      return null;
    }
  }, []);

  useEffect(() => {
    loadAzureSettings();
  }, [loadAzureSettings]);

  const uploadFileToAzure = useCallback(async (file) => {
    if (!azureSettings) throw new Error('Azure settings not loaded');
    const { AZURE_BLOB_BASE_URL, AZURE_SAS_TOKEN } = azureSettings;
    const containerUrl = AZURE_BLOB_BASE_URL + AZURE_SAS_TOKEN;
    const blobServiceClient = new BlobServiceClient(containerUrl);
    const containerClient = blobServiceClient.getContainerClient("");
    const ext = (file.name?.split('.').pop() || 'png').toLowerCase();
    const randomTxt = `${Math.random().toString(36).substring(2, 8)}${Date.now()}`;
    const newFileName = `test_input_${randomTxt}.${ext}`;
    const blockBlobClient = containerClient.getBlockBlobClient(newFileName);
    const { publicUrl } = await blockBlobClient.uploadBrowserData(file, {
      blobHTTPHeaders: { blobContentType: file.type || 'image/png' },
    });
    return publicUrl;
  }, [azureSettings]);

  const ensureAzure = useCallback(async () => {
    if (!azureSettings) {
      const settings = await loadAzureSettings();
      if (!settings) throw new Error('Azure settings not available');
    }
  }, [azureSettings, loadAzureSettings]);

  const computeKey = (file) => (file ? `${file.name}|${file.size}|${file.lastModified}` : '');

  const handleDropFaces = useCallback((droppedFiles, acceptedFiles) => {
    const files = acceptedFiles || droppedFiles || [];
    setFaceFiles(files);
  }, []);



  const handleDropMask = useCallback((droppedFiles, acceptedFiles) => {
    const files = acceptedFiles || droppedFiles || [];
    setMaskFiles(files);
    const first = files?.[0];
    const newKey = computeKey(first);
    if (newKey !== maskFileKey) {
      setMaskAzureUrl('');
      setMaskFileKey(newKey);
    }
    if (files.length > 0) setMaskUrl('');
  }, [maskFileKey]);

  const maskFirstPreviewUrl = useMemo(() => maskFiles?.[0] ? URL.createObjectURL(maskFiles[0]) : undefined, [maskFiles]);
  const faceFilesPreviewUrls = useMemo(() => faceFiles?.map(f => URL.createObjectURL(f)) || [], [faceFiles]);

  useEffect(() => () => {
    if (maskFirstPreviewUrl) URL.revokeObjectURL(maskFirstPreviewUrl);
    faceFilesPreviewUrls.forEach(u => URL.revokeObjectURL(u));
  }, [maskFirstPreviewUrl, faceFilesPreviewUrls]);

  const getFinalUrl = useCallback(async (typedUrl, files, cachedUrl, cachedKey, setCachedUrl, setCachedKey) => {
    const direct = (typedUrl || '').trim();
    if (direct) return direct;
    if (!files || files.length === 0) return '';
    const first = files[0];
    const currentKey = computeKey(first);
    if (cachedUrl && currentKey === cachedKey) {
      return cachedUrl;
    }
    await ensureAzure();
    const uploaded = await uploadFileToAzure(first);
    setCachedUrl(uploaded);
    setCachedKey(currentKey);
    return uploaded;
  }, [ensureAzure, uploadFileToAzure]);

  const getFinalFaces = useCallback(async () => {
    const final = [];
    // Collect typed URLs
    facesUrls.forEach(u => {
      const t = (u || '').trim();
      if (t) final.push(t);
    });
    // Collect uploaded files
    if (faceFiles && faceFiles.length > 0) {
      await ensureAzure();
      const newMap = { ...faceAzureMap };
      for (const f of faceFiles) {
        const k = computeKey(f);
        if (newMap[k]) {
          final.push(newMap[k]);
        } else {
          const up = await uploadFileToAzure(f);
          newMap[k] = up;
          final.push(up);
        }
      }
      setFaceAzureMap(newMap);
    }
    return final;
  }, [facesUrls, faceFiles, faceAzureMap, ensureAzure, uploadFileToAzure]);

  const notify = useCallback((message) => {
    if (typeof setToastMessage === 'function' && typeof setToastActive === 'function') {
      setToastMessage(message);
      setToastActive(true);
      setTimeout(() => setToastActive(false), 2000);
    }
  }, [setToastMessage, setToastActive]);

  const handleDownloadPreview = useCallback(async () => {
    if (!selectedPreviewUrl) return;
    try {
      const resp = await fetch(selectedPreviewUrl, { mode: 'cors' });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filename = (selectedPreviewUrl.split('/').pop() || 'image').split('?')[0] || 'image';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_ignore) {}
  }, [selectedPreviewUrl]);

  const handleGenerate = useCallback(async () => {
    if (isGenerating) return;
    const baseUrl = getServerURL();
    try {
      setIsGenerating(true);

      const finalFaces = await getFinalFaces();
      const finalMaskUrl = await getFinalUrl(maskUrl, maskFiles, maskAzureUrl, maskFileKey, setMaskAzureUrl, setMaskFileKey);

      if (!finalFaces.length || !finalMaskUrl) {
        notify('Please provide Faces and Mask images');
        setIsGenerating(false);
        return;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 180000); // 3 minutes timeout

      const resp = await fetch(`${baseUrl}/test-scene-multiple`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          faces: finalFaces,
          mask: finalMaskUrl,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      let data = null;
      try {
        data = await resp.clone().json();
      } catch (_ignore) {}

      if (!resp.ok) {
        const msg = data?.message || `Generation failed (${resp.status})`;
        notify(msg);
        setIsGenerating(false);
        return;
      }

      const successful = Array.isArray(data?.successfulResults) ? data.successfulResults : [];
      setResults(successful);
      if (successful.length > 0) {
        const first = successful.find(r => r?.success && r?.imageUrl)?.imageUrl || successful[0]?.imageUrl || '';
        if (first) setSelectedPreviewUrl(first);
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        notify('Request timed out after 3 minutes');
      } else {
        notify('Unexpected error while generating');
      }
    } finally {
      setIsGenerating(false);
    }
  }, [isGenerating, getFinalFaces, maskUrl, maskFiles, maskAzureUrl, maskFileKey, getFinalUrl, notify]);

  const renderInputBlock = (label, urlValue, setUrlValue, files, setFiles, firstPreviewUrl, onDrop) => (
    <>
      <TextField
        label={`${label} URL`}
        value={urlValue}
        onChange={(value) => {
          if (value && files.length > 0) {
            setFiles([]);
          }
          setUrlValue(value);
        }}
        placeholder="https://example.com/image.png"
        autoComplete="off"
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0 8px' }}>
        <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
        <Text as="span" tone="subdued">-OR-</Text>
        <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
      </div>

      <DropZone accept="image/*" allowMultiple={false} onDrop={onDrop} variableHeight>
        <div style={{ padding: 16, textAlign: 'center', width: '100%' }}>
          {firstPreviewUrl ? (
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <button
                onClick={() => setFiles([])}
                style={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: 28,
                  height: 28,
                  cursor: 'pointer',
                }}
                aria-label={`Remove ${label} file`}
              >
                ×
              </button>
              <img src={firstPreviewUrl} alt={`${label} preview`} style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 12 }} />
            </div>
          ) : (
            <InlineStack align="center" gap="300">
              <Icon source={UploadMajor} tone="base" />
              <Text as="span" tone="subdued">Drop {label.toLowerCase()} image here or click to upload</Text>
            </InlineStack>
          )}
        </div>
      </DropZone>
    </>
  );

  const addFaceUrlField = () => setFacesUrls(prev => [...prev, '']);
  const updateFaceUrl = (index, value) => {
    setFacesUrls(prev => prev.map((v, i) => (i === index ? value : v)));
  };
  const removeFaceUrlField = (index) => {
    setFacesUrls(prev => prev.filter((_, i) => i !== index));
  };

  const removeFaceFileAt = (index) => {
    setFaceFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <InlineStack gap="400" wrap={false}>
        <Box
          padding="400"
          borderWidth="0"
          borderColor="transparent"
          borderRadius="0"
          minHeight="1024px"
          style={{ width: '33.3333%', maxHeight: embedded ? '70vh' : '100vh', display: 'flex', flexDirection: 'column', gap: 16, overflow: 'auto' }}
          className="Polaris-ShadowBevel"
        >
          <div style={{ marginBottom: 12 }}>
            <Text variant="headingMd" as="h2">Test Scene Inputs</Text>
          </div>

          {/* Faces (Multiple) */}
          <div>
            <Text variant="headingSm" as="h3">Faces</Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
              {facesUrls.map((value, idx) => (
                <div key={`face-url-${idx}`} style={{ position: 'relative' }}>
                  <TextField
                    label={`Face URL #${idx + 1}`}
                    value={value}
                    onChange={(v) => updateFaceUrl(idx, v)}
                    placeholder="https://example.com/face.png"
                    autoComplete="off"
                  />
                  {facesUrls.length > 1 && (
                    <button
                      onClick={() => removeFaceUrlField(idx)}
                      style={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: 28,
                        height: 28,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        fontSize: 18,
                        fontWeight: 'bold',
                        zIndex: 2,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                      }}
                      aria-label="Remove face URL"
                      title="Remove face URL"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <InlineStack gap="200">
                <Button onClick={addFaceUrlField}>Add Face URL</Button>
                <Button onClick={() => setFacesUrls([''])} variant="tertiary">Clear Face URLs</Button>
              </InlineStack>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '8px 0' }}>
              <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
              <Text as="span" tone="subdued">-OR-</Text>
              <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
            </div>

            <DropZone accept="image/*" allowMultiple={true} onDrop={handleDropFaces} variableHeight>
              <div style={{ padding: 16 }}>
                {faceFiles.length > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8 }}>
                    {faceFilesPreviewUrls.map((u, i) => (
                      <div key={`face-file-${i}`} style={{ position: 'relative' }}>
                        <button
                          onClick={() => removeFaceFileAt(i)}
                          style={{
                            position: 'absolute',
                            top: -6,
                            right: -6,
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: 24,
                            height: 24,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: 16,
                            fontWeight: 'bold',
                            zIndex: 2,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                          }}
                          aria-label="Remove face file"
                          title="Remove face file"
                        >
                          ×
                        </button>
                        <img src={u} alt={`Face ${i + 1}`} style={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 8 }} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <Icon source={UploadMajor} tone="base" />
                    <Text as="p" variant="bodyMd" tone="subdued">
                      Drag and drop face images here, or click to upload (multiple)
                    </Text>
                  </div>
                )}
              </div>
            </DropZone>
          </div>

          {renderInputBlock('Mask Image', maskUrl, setMaskUrl, maskFiles, setMaskFiles, maskFirstPreviewUrl, handleDropMask)}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button icon={MagicMinor} variant="primary" loading={isGenerating} onClick={handleGenerate} style={{ minWidth: 140 }}>
              Generate
            </Button>
          </div>
        </Box>

        <Box
          padding="400"
          borderWidth="0"
          borderColor="transparent"
          borderRadius="0"
          minHeight="1024px"
          style={{ width: '66.6667%', maxHeight: embedded ? '70vh' : '100vh', overflow: 'hidden' }}
          className="Polaris-ShadowBevel"
        >
          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Text variant="headingMd" as="h2">Preview</Text>
            <InlineStack gap="200" align="center">
              <Button icon={ImportMinor} onClick={handleDownloadPreview} disabled={!selectedPreviewUrl}>
                Download
              </Button>
            </InlineStack>
          </div>
          <div
            ref={previewContainerRef}
            style={{
              width: '100%',
              height: results?.length ? '540px' : 'calc(100% - 40px)',
              background: '#f5f6f7',
              borderRadius: 12,
              overflow: 'auto',
              display: 'flex',
              alignItems: selectedPreviewUrl ? 'flex-start' : 'center',
              justifyContent: selectedPreviewUrl ? 'flex-start' : 'center',
              position: 'relative',
              padding: selectedPreviewUrl ? '8px' : '0',
            }}
          >
            {selectedPreviewUrl ? (
              <img
                src={selectedPreviewUrl}
                alt="Preview"
                style={{
                  maxWidth: 'none',
                  maxHeight: 'none',
                  minWidth: '100%',
                  minHeight: '100%',
                  objectFit: 'contain',
                  borderRadius: 8,
                }}
              />
            ) : (
              <Text tone="subdued">No image generated yet</Text>
            )}
            {isGenerating && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spinner size="large" />
              </div>
            )}
          </div>

          {/* Results Grid */}
          {results && results.length > 0 && (
            <div style={{ marginTop: 16, maxHeight: '300px', overflow: 'auto' }}>
              <Text variant="headingSm" as="h3" style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1, paddingBottom: 8 }}>Results</Text>
              <div style={{ 
                marginTop: 8, 
                overflowX: 'auto', 
                paddingBottom: 8,
                scrollbarWidth: 'thin',
                WebkitOverflowScrolling: 'touch'
              }}>
                <div style={{ display: 'flex', gap: 12, minWidth: 'max-content', paddingRight: 8 }}>
                  {results.map((r, idx) => (
                    <div
                      key={`res-${idx}`}
                      style={{
                        cursor: 'pointer',
                        border: selectedPreviewUrl === r?.imageUrl ? '2px solid #0EA5E9' : '1px solid #E5E7EB',
                        borderRadius: 10,
                        padding: 6,
                        background: '#fff',
                        minWidth: 140,
                        flexShrink: 0
                      }}
                      onClick={() => setSelectedPreviewUrl(r?.imageUrl)}
                      title={r?.imageUrl || 'Result image'}
                    >
                      <img src={r?.imageUrl} alt={`Result ${idx}`} style={{ width: 140, height: 140, objectFit: 'cover', borderRadius: 6 }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Box>
      </InlineStack>
    </div>
  );
};

export default AIDesignMultipleTestPage;